import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import ViewAndDownloadFileStatsTable from './components/ViewAndDownloadFileStatsTable';
import {
  SORTING_VALUES,
  ViewAndDownloadFileStatsTableData,
} from './components/ViewAndDownloadFileStatsTable/data';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import usePageParams from 'hooks/usePageParams';
import { Params } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import { FIRST_PAGE } from 'data/global';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import { getNodeMimeType, getUserFullName } from 'utils';

import './ViewAndDownloadFileStatsModal.styles.scss';

const LIMIT = '10';
const PAGE_PARAM = 'viewAndDownloadPage';

type ViewAndDownloadFileStatsModalProps = {
  className?: string;
  dataRoomNode: DataRoomNode;
} & ModalProps;

const ViewAndDownloadFileStatsModal: React.ForwardRefRenderFunction<
  ModalRef,
  ViewAndDownloadFileStatsModalProps
> = (props, ref) => {
  const { className, dataRoomNode, ...rest } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<ViewAndDownloadFileStatsTableData[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [sort, setSort] = useState(SORTING_VALUES.numberOfViews);

  const { popServerError } = useContext(PopNotificationsContext);

  const { setQueryParam } = useQueryParams<Params>();

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('view-and-download-file-stats-modal', className);

  const page = usePageParams(PAGE_PARAM);

  const onClose = useCallback(() => {
    setData([]);
    setTotalPages(1);
    setSort(SORTING_VALUES.numberOfViews);
    setQueryParam(PAGE_PARAM, FIRST_PAGE);
  }, [setQueryParam]);

  useEffect(() => {
    if (!modal?.openState || !dataRoomNode?._id) return;

    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await dataRoomApi.reports.getReportsForNode(dataRoomNode?._id, {
          $sort: sort,
          $page: page,
          $limit: LIMIT,
        });

        setData(
          items.map(({ dataRoomMember, numberOfDownloads, numberOfViews }) => ({
            numberOfDownloads,
            numberOfViews,
            dataRoomMemberName: getUserFullName(
              dataRoomMember?.firstName,
              dataRoomMember?.lastName,
            ),
          })),
        );
        setTotalPages(totalPages);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dataRoomNode, modal?.openState, page, popServerError, sort]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      onClose={onClose}
      title={
        <div className="file-name">
          <MymeTypeIcon mimeType={getNodeMimeType(dataRoomNode)} />
          <span>
            <span>{dataRoomNode?.document?.name}</span>
          </span>
        </div>
      }
      {...rest}
    >
      <ViewAndDownloadFileStatsTable
        isLoading={isLoading}
        data={data}
        totalPages={totalPages}
        numberOfPlaceholderRows={+LIMIT}
        pageParam={PAGE_PARAM}
        sort={sort}
        updateSort={setSort}
      />
    </Modal>
  );
};

export default forwardRef(ViewAndDownloadFileStatsModal);
